/**
 * Shared formatting for the hand-built citation generators.
 *
 * Most source types go through the CSL engine (`<CitationTool>`), which knows every
 * style. A handful cannot: a constitution is cited by article and section, an "as cited
 * in" reference is two sources folded into one, an APA figure note is not a reference
 * at all. Those generators format APA 7, MLA 9 and Chicago 18 themselves, and these are
 * the pieces they share — so a name is inverted the same way, a date is abbreviated the
 * same way and et al. starts at the same author on every one of them.
 *
 * Output is a list of `CitationPart`s rather than a string so italics survive: the
 * widget renders them as `<i>`, copies them as HTML for Word, and as plain text for
 * everything else.
 *
 * Rules, with the edition they come from:
 * - APA 7 (2020): references list up to 20 authors (21+: first 19, ellipsis, last);
 *   in-text uses et al. from three authors.
 * - MLA 9 (2021): Works Cited gives one or two authors in full and "et al." from three;
 *   months longer than four letters are abbreviated (Jan., Feb., Aug., Sept., Oct.,
 *   Nov., Dec.; March, April, May, June, July stay whole).
 * - Chicago 18 (2024): a bibliography lists up to six authors and, for seven or more,
 *   the first three followed by et al.; a note lists up to two and uses et al. from
 *   three.
 */

export interface CitationPart {
  text: string;
  italic?: boolean;
}

export type CitationParts = CitationPart[];

/** Plain text, for plain-text targets and for comparing outputs in tests. */
export function partsToPlain(parts: CitationParts): string {
  return parts.map((part) => part.text).join("");
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
const RE_HTML_SPECIAL = /[&<>"']/g;

export function escapeHtml(text: string): string {
  return text.replace(RE_HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

/** HTML with `<i>` for italics — what `CopyButton`'s `html` prop wants. */
export function partsToHtml(parts: CitationParts): string {
  return parts
    .map((part) =>
      part.italic ? `<i>${escapeHtml(part.text)}</i>` : escapeHtml(part.text)
    )
    .join("");
}

/**
 * Collapse the seams left by optional fields: doubled spaces, a space before
 * punctuation, and the ".." an abbreviation ending in a period produces when a field
 * separator adds another.
 */
export function tidyParts(parts: CitationParts): CitationParts {
  const tidied: CitationParts = [];
  for (const part of parts) {
    if (part.text === "") {
      continue;
    }
    tidied.push({ ...part });
  }
  for (let index = 0; index < tidied.length; index++) {
    const part = tidied[index];
    part.text = part.text.replace(RE_MULTI_SPACE, " ");
    const previous = tidied[index - 1];
    if (!previous) {
      continue;
    }
    if (
      previous.text.trimEnd().endsWith(".") &&
      part.text.trimStart().startsWith(".")
    ) {
      part.text = part.text.trimStart().slice(1);
    }
    if (previous.text.endsWith(" ") && part.text.startsWith(" ")) {
      part.text = part.text.trimStart();
    }
    if (previous.text.endsWith(" ") && RE_LEADING_PUNCT.test(part.text)) {
      previous.text = previous.text.trimEnd();
    }
  }
  const first = tidied[0];
  if (first) {
    first.text = first.text.trimStart();
  }
  const last = tidied.at(-1);
  if (last) {
    last.text = last.text.trimEnd();
  }
  return tidied.filter((part) => part.text !== "");
}

const RE_MULTI_SPACE = / {2,}/g;
const RE_LEADING_PUNCT = /^[.,;:]/;
const RE_TERMINAL = /[.?!]["”’)]*$/;

/** Add a period unless the text already ends a sentence ("?" titles keep theirs). */
export function withPeriod(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "" || RE_TERMINAL.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}.`;
}

/* --------------------------------------------------------------------- names */

const RE_WHITESPACE = /\s+/;
const RE_TRAILING_COMMA = /,+$/;
const RE_HYPHEN = /-/;

const SUFFIXES = new Set([
  "jr",
  "jr.",
  "sr",
  "sr.",
  "ii",
  "iii",
  "iv",
  "v",
  "2nd",
  "3rd",
  "4th",
]);

/** Surname particles that stay attached to the family name ("van Gogh"). */
const PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "der",
  "den",
  "di",
  "da",
  "du",
  "la",
  "le",
  "los",
  "las",
  "ter",
  "ten",
  "op",
  "het",
  "el",
  "al",
  "bin",
  "ibn",
  "af",
  "av",
  "zu",
]);

export interface PersonName {
  /** Given names in order: ["Mary", "Jane"]. Empty for a mononym or an organization. */
  given: string[];
  /** Family name with its particles: "van Gogh". For an organization, its full name. */
  family: string;
  suffix?: string;
  /** A group author — "World Health Organization" — never inverted or initialised. */
  organization?: boolean;
}

function isSuffixToken(token: string): boolean {
  return SUFFIXES.has(token.toLowerCase().replace(RE_TRAILING_COMMA, ""));
}

function displaySuffix(token: string): string {
  const bare = token.replace(RE_TRAILING_COMMA, "");
  const lower = bare.toLowerCase();
  if (lower === "jr" || lower === "jr.") {
    return "Jr.";
  }
  if (lower === "sr" || lower === "sr.") {
    return "Sr.";
  }
  if (["ii", "iii", "iv", "v"].includes(lower)) {
    return bare.toUpperCase();
  }
  return bare;
}

/** "Smith, Mary Jane" or "Smith, Mary Jane, Jr." — the comma says which is which. */
function parseInvertedName(trimmed: string): PersonName | null {
  const parts = trimmed
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return null;
  }
  // "Martin Luther King, Jr." — the comma sets off a suffix, not the given names. A lone
  // "V" stays an initial ("Smith, V").
  if (parts.length === 2 && parts[1].length > 1 && isSuffixToken(parts[1])) {
    return parseNaturalName(`${parts[0]} ${parts[1]}`);
  }
  let given = parts[1].split(RE_WHITESPACE).filter(Boolean);
  let suffix: string | undefined;
  if (parts.length >= 3 && isSuffixToken(parts[2])) {
    suffix = displaySuffix(parts[2]);
  } else if (given.length >= 2 && isSuffixToken(given.at(-1) ?? "")) {
    suffix = displaySuffix(given.at(-1) ?? "");
    given = given.slice(0, -1);
  }
  return { given, family: parts[0], suffix };
}

/** "Mary Jane Smith", "Vincent van Gogh", "Martin Luther King Jr." */
function parseNaturalName(trimmed: string): PersonName {
  let tokens = trimmed.split(RE_WHITESPACE).filter(Boolean);
  let suffix: string | undefined;
  if (tokens.length >= 2 && isSuffixToken(tokens.at(-1) ?? "")) {
    suffix = displaySuffix(tokens.at(-1) ?? "");
    tokens = tokens
      .slice(0, -1)
      .map((token) => token.replace(RE_TRAILING_COMMA, ""));
  }
  if (tokens.length === 1) {
    return { given: [], family: tokens[0], suffix };
  }
  let familyStart = tokens.length - 1;
  while (
    familyStart > 0 &&
    PARTICLES.has(tokens[familyStart - 1].toLowerCase())
  ) {
    familyStart -= 1;
  }
  if (familyStart === 0) {
    familyStart = tokens.length - 1;
  }
  return {
    given: tokens.slice(0, familyStart),
    family: tokens.slice(familyStart).join(" "),
    suffix,
  };
}

/**
 * Read one person's name as typed: "Mary Jane Smith", "Smith, Mary Jane",
 * "Martin Luther King Jr.", "Vincent van Gogh". A single token is a family name.
 */
export function parsePersonName(input: string): PersonName | null {
  const trimmed = input.trim();
  if (trimmed === "") {
    return null;
  }
  if (trimmed.includes(",")) {
    const inverted = parseInvertedName(trimmed);
    if (inverted) {
      return inverted;
    }
  }
  return parseNaturalName(trimmed);
}

/** A group author, kept exactly as written. */
export function organizationName(name: string): PersonName {
  return { given: [], family: name.trim(), organization: true };
}

/** "Jean-Michel" → "J.-M.", "John" → "J.", an existing initial "J." → "J." */
function initial(given: string): string {
  return given
    .split(RE_HYPHEN)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join("-");
}

function withSuffix(base: string, suffix: string | undefined): string {
  return suffix ? `${base}, ${suffix}` : base;
}

/** APA reference form: "Smith, M. J." — organizations as written. */
export function apaName(name: PersonName): string {
  if (name.organization || name.given.length === 0) {
    return withSuffix(name.family, name.suffix);
  }
  return withSuffix(
    `${name.family}, ${name.given.map(initial).join(" ")}`,
    name.suffix
  );
}

/** "M. J. Smith" — APA's form for editors, narrators and other non-author roles. */
export function apaNameInitialsFirst(name: PersonName): string {
  if (name.organization || name.given.length === 0) {
    return name.family;
  }
  const base = `${name.given.map(initial).join(" ")} ${name.family}`;
  return name.suffix ? `${base}, ${name.suffix}` : base;
}

/** "Smith, Mary Jane" — first author in MLA and in a Chicago bibliography. */
export function invertedName(name: PersonName): string {
  if (name.organization || name.given.length === 0) {
    return withSuffix(name.family, name.suffix);
  }
  return withSuffix(`${name.family}, ${name.given.join(" ")}`, name.suffix);
}

/** "Mary Jane Smith" — later authors in MLA/Chicago, and every name in a note. */
export function naturalName(name: PersonName): string {
  if (name.organization || name.given.length === 0) {
    return name.suffix ? `${name.family}, ${name.suffix}` : name.family;
  }
  const base = `${name.given.join(" ")} ${name.family}`;
  return name.suffix ? `${base}, ${name.suffix}` : base;
}

/** The surname alone, for in-text citations and shortened notes. */
export function familyName(name: PersonName): string {
  return name.family;
}

/** APA 7 reference list: up to 20 authors; 21 or more → first 19, "…", last. */
export function apaAuthors(names: PersonName[]): string {
  const formatted = names.map(apaName);
  if (formatted.length === 0) {
    return "";
  }
  if (formatted.length === 1) {
    return formatted[0];
  }
  if (formatted.length === 2) {
    return `${formatted[0]}, & ${formatted[1]}`;
  }
  if (formatted.length >= 21) {
    return `${formatted.slice(0, 19).join(", ")}, . . . ${formatted.at(-1)}`;
  }
  return `${formatted.slice(0, -1).join(", ")}, & ${formatted.at(-1)}`;
}

/** APA 7 in-text: "(Smith, 2020)", "(Smith & Lee, 2020)", "(Smith et al., 2020)". */
export function apaInTextAuthors(
  names: PersonName[],
  narrative = false
): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return familyName(names[0]);
  }
  if (names.length === 2) {
    const joiner = narrative ? " and " : " & ";
    return `${familyName(names[0])}${joiner}${familyName(names[1])}`;
  }
  return `${familyName(names[0])} et al.`;
}

/** MLA 9 Works Cited: one author inverted; two joined with "and"; three+ "et al." */
export function mlaAuthors(names: PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return invertedName(names[0]);
  }
  if (names.length === 2) {
    return `${invertedName(names[0])}, and ${naturalName(names[1])}`;
  }
  return `${invertedName(names[0])}, et al.`;
}

/** MLA 9 in-text: "(Smith)", "(Smith and Lee)", "(Smith et al.)". */
export function mlaInTextAuthors(names: PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return familyName(names[0]);
  }
  if (names.length === 2) {
    return `${familyName(names[0])} and ${familyName(names[1])}`;
  }
  return `${familyName(names[0])} et al.`;
}

function serialList(items: string[]): string {
  if (items.length <= 1) {
    return items.join("");
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}

/**
 * Chicago 18 bibliography: up to six authors listed (first inverted); seven or more →
 * the first three, then "et al."
 */
export function chicagoBibliographyAuthors(names: PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return invertedName(names[0]);
  }
  if (names.length > 6) {
    return `${invertedName(names[0])}, ${naturalName(names[1])}, ${naturalName(names[2])}, et al.`;
  }
  const [first, ...rest] = names;
  if (rest.length === 1) {
    return `${invertedName(first)}, and ${naturalName(rest[0])}`;
  }
  return `${invertedName(first)}, ${rest
    .slice(0, -1)
    .map(naturalName)
    .join(", ")}, and ${naturalName(rest.at(-1) as PersonName)}`;
}

/** Chicago 18 note: one or two authors in full; three or more → first "et al." */
export function chicagoNoteAuthors(names: PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length >= 3) {
    return `${naturalName(names[0])} et al.`;
  }
  return serialList(names.map(naturalName));
}

/** Chicago 18 shortened note / author-date: surnames; three or more → "et al." */
export function chicagoShortAuthors(names: PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length >= 3) {
    return `${familyName(names[0])} et al.`;
  }
  return serialList(names.map(familyName));
}

/* --------------------------------------------------------------------- dates */

export interface PartialDate {
  year: number;
  /** 1–12 */
  month?: number;
  day?: number;
}

const RE_ISO_DATE = /^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?$/;

/**
 * Read "2024", "2024-03" or "2024-03-05" — what an `<input type="date">` or a year
 * field gives you. Returns null for anything else, so a generator can fall back to
 * "n.d." rather than print a malformed date.
 */
export function parsePartialDate(input: string): PartialDate | null {
  const match = RE_ISO_DATE.exec(input.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) : undefined;
  const day = match[3] ? Number(match[3]) : undefined;
  if (month !== undefined && (month < 1 || month > 12)) {
    return null;
  }
  if (day !== undefined && (day < 1 || day > 31)) {
    return null;
  }
  return { year, month, day };
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** MLA 9 month abbreviations (MLA Handbook 9th ed., appendix 1). */
const MLA_MONTHS = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May",
  "June",
  "July",
  "Aug.",
  "Sept.",
  "Oct.",
  "Nov.",
  "Dec.",
];

/** APA: "2024, March 5" — wrap in parentheses yourself. Null date → "n.d.". */
export function apaDate(date: PartialDate | null): string {
  if (!date) {
    return "n.d.";
  }
  if (!date.month) {
    return String(date.year);
  }
  const month = MONTHS[date.month - 1];
  return date.day
    ? `${date.year}, ${month} ${date.day}`
    : `${date.year}, ${month}`;
}

/** MLA: "5 Mar. 2024", "Mar. 2024", "2024". */
export function mlaDate(date: PartialDate | null): string {
  if (!date) {
    return "";
  }
  if (!date.month) {
    return String(date.year);
  }
  const month = MLA_MONTHS[date.month - 1];
  return date.day
    ? `${date.day} ${month} ${date.year}`
    : `${month} ${date.year}`;
}

/** Chicago: "March 5, 2024", "March 2024", "2024". */
export function chicagoDate(date: PartialDate | null): string {
  if (!date) {
    return "n.d.";
  }
  if (!date.month) {
    return String(date.year);
  }
  const month = MONTHS[date.month - 1];
  return date.day
    ? `${month} ${date.day}, ${date.year}`
    : `${month} ${date.year}`;
}

/** The year alone for in-text citations; "n.d." when there is no date. */
export function yearOf(date: PartialDate | null): string {
  return date ? String(date.year) : "n.d.";
}
